<script setup lang="ts">
import { JOB_PACK_DAYS } from '~/constants/pricing'

/**
 * The guide half of /ai-cover-letter, rendered above the product showcase.
 *
 * The page used to open straight into the generator, which answered none of the
 * questions people actually type ("what is a cover letter", "how to format a
 * cover letter", "who do you address a cover letter to"). Now the answer comes
 * first and the generator sits underneath it as the obvious next step.
 *
 * /templates carries a four-paragraph skeleton and links here; this page is the
 * deeper one, so the two must stay in that order and not repeat each other.
 */
const { t } = useI18n()
const { jobPackPrice } = usePremiumPlans()

const formatRules = ['length', 'header', 'spacing', 'file'] as const
const findNameSteps = ['posting', 'linkedin', 'site'] as const
const opensGood = ['good1', 'good2', 'good3'] as const
const opensBad = ['bad1', 'bad2', 'bad3'] as const
const closesGood = ['good1', 'good2'] as const
const closesBad = ['bad1', 'bad2', 'bad3'] as const
const include = ['role', 'why', 'proof', 'fit', 'gap', 'close'] as const
const omit = ['summary', 'adjectives', 'salary', 'personal', 'generic'] as const

/** Each paragraph of the worked letter with the note explaining its job. */
const letter = [
  { id: 'p1', note: 'open' },
  { id: 'p2', note: 'proof' },
  { id: 'p3', note: 'fit' },
  { id: 'p4', note: 'close' },
] as const

const signoff = computed(() => t('coverLetterGuide.example.body.signoff').split('\n'))
</script>

<template>
  <div class="sofax">
    <!-- The answer, before the product -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-sm">
        <div class="shell">
          <div class="mx-auto max-w-3xl rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
            <div class="mb-3 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
              {{ t('coverLetterGuide.short.label') }}
            </div>
            <p class="text-lg">{{ t('coverLetterGuide.short.text') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- What it is for, and the honest answer on whether you need one -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-bottom">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('coverLetterGuide.what.eyebrow')"
            :title="t('coverLetterGuide.what.title')"
            width="max-w-4xl"
          />
          <div class="mx-auto max-w-3xl">
            <p class="text-lg">{{ t('coverLetterGuide.what.p1') }}</p>
            <p class="mt-6 text-lg">{{ t('coverLetterGuide.what.p2') }}</p>

            <div class="mt-10 rounded-[0.625rem] bg-ink p-[1.875rem] text-white dark:bg-gray-800">
              <div class="display-heading display-heading-4 mb-4 !text-white">
                {{ t('coverLetterGuide.what.necessaryTitle') }}
              </div>
              <p class="!text-white/80">{{ t('coverLetterGuide.what.necessaryText') }}</p>
              <p class="mt-6">
                <LocaleLink to="/blog/cover-letter-vs-no-cover-letter-do-employers-still-care" class="link !text-white">
                  {{ t('coverLetterGuide.what.link') }} →
                </LocaleLink>
              </p>
            </div>

            <!-- UK public sector asks for a scored statement, not a letter -->
            <p class="mt-6 !text-base">
              {{ t('coverLetterGuide.what.ukLead') }}
              <LocaleLink to="/civil-service-application" class="link">{{ t('coverLetterGuide.what.ukCivil') }}</LocaleLink>,
              <LocaleLink to="/nhs-supporting-statement" class="link">{{ t('coverLetterGuide.what.ukNhs') }}</LocaleLink>.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Format: the boring answers, given plainly -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('coverLetterGuide.format.eyebrow')"
            :title="t('coverLetterGuide.format.title')"
            :text="t('coverLetterGuide.format.lead')"
            width="max-w-4xl"
          />

          <ul class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <li v-for="rule in formatRules" :key="rule" class="h-full rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800">
              <div class="display-heading display-heading-5 mb-3">
                {{ t(`coverLetterGuide.format.rules.${rule}.title`) }}
              </div>
              <p class="text-base">{{ t(`coverLetterGuide.format.rules.${rule}.text`) }}</p>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Salutation: three named queries, one short section -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('coverLetterGuide.address.eyebrow')"
            :title="t('coverLetterGuide.address.title')"
            :text="t('coverLetterGuide.address.lead')"
            width="max-w-4xl"
          />

          <div class="mx-auto grid max-w-5xl grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-16">
            <ol class="flex flex-col gap-5">
              <li v-for="(step, index) in findNameSteps" :key="step" class="flex items-start gap-4">
                <span
                  class="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-ink !text-base !font-bold !text-white dark:bg-white dark:!text-ink"
                  aria-hidden="true"
                >{{ index + 1 }}</span>
                <p class="pt-1 text-base">{{ t(`coverLetterGuide.address.steps.${step}`) }}</p>
              </li>
            </ol>

            <div class="card">
              <div class="display-heading display-heading-5 mb-5">{{ t('coverLetterGuide.address.fallbackTitle') }}</div>
              <p class="!mb-4 flex items-start gap-3 text-base">
                <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-lg text-accent" />
                <span>{{ t('coverLetterGuide.address.fallbackGood') }}</span>
              </p>
              <p class="flex items-start gap-3 text-base">
                <i class="bi bi-x-circle mt-0.5 shrink-0 text-lg text-muted" />
                <span>{{ t('coverLetterGuide.address.fallbackBad') }}</span>
              </p>
            </div>
          </div>

          <p class="mx-auto mt-8 max-w-5xl text-base text-muted dark:text-gray-400">{{ t('coverLetterGuide.address.note') }}</p>
        </div>
      </div>
    </section>

    <!-- The worked letter, annotated. The strongest asset on the page. -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('coverLetterGuide.example.eyebrow')"
            :title="t('coverLetterGuide.example.title')"
            :text="t('coverLetterGuide.example.lead')"
            width="max-w-4xl"
          />

          <article class="mx-auto max-w-3xl rounded-[0.625rem] bg-white p-8 sm:p-10 dark:bg-gray-800">
            <p class="!mb-6 text-base font-semibold text-ink dark:text-white">
              {{ t('coverLetterGuide.example.body.salutation') }}
            </p>

            <div v-for="para in letter" :key="para.id" class="mb-8 last:mb-0">
              <p class="!mb-2 text-base">{{ t(`coverLetterGuide.example.body.${para.id}`) }}</p>
              <p class="border-l-2 border-accent pl-4 text-sm text-muted dark:text-gray-400">
                {{ t(`coverLetterGuide.example.annotations.${para.note}`) }}
              </p>
            </div>

            <p v-for="(line, i) in signoff" :key="i" class="text-base" :class="i === 0 ? 'mt-8' : 'font-semibold text-ink dark:text-white'">
              {{ line }}
            </p>
          </article>

          <p class="mx-auto mt-4 max-w-3xl text-sm text-muted dark:text-gray-400">{{ t('coverLetterGuide.example.caption') }}</p>

          <div class="mx-auto mt-8 max-w-3xl rounded-[0.625rem] border-l-4 border-accent bg-white p-[1.875rem] dark:bg-gray-800">
            <p>{{ t('coverLetterGuide.example.honestNote') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Openings and closings, as lines you can steal or delete -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('coverLetterGuide.openClose.eyebrow')"
            :title="t('coverLetterGuide.openClose.title')"
            width="max-w-4xl"
          />

          <div class="mx-auto grid max-w-5xl grid-cols-1 gap-6 lg:grid-cols-2">
            <div v-for="group in [{ id: 'opens', title: 'openTitle', good: opensGood, bad: opensBad }, { id: 'closes', title: 'closeTitle', good: closesGood, bad: closesBad }]" :key="group.id" class="card h-full">
              <div class="display-heading display-heading-4 mb-6">{{ t(`coverLetterGuide.openClose.${group.title}`) }}</div>

              <div class="mb-2 flex items-center gap-2 text-base font-semibold text-ink dark:text-white">
                <i class="bi bi-check-circle-fill shrink-0 text-lg text-accent" />
                {{ t('coverLetterGuide.openClose.goodLabel') }}
              </div>
              <ul class="mb-6 flex flex-col gap-3">
                <li v-for="key in group.good" :key="key" class="border-l-2 border-accent pl-4 text-base italic">
                  {{ t(`coverLetterGuide.openClose.${group.id}.${key}`) }}
                </li>
              </ul>

              <div class="mb-2 flex items-center gap-2 text-base font-semibold text-ink dark:text-white">
                <i class="bi bi-x-circle shrink-0 text-lg text-muted" />
                {{ t('coverLetterGuide.openClose.badLabel') }}
              </div>
              <ul class="flex flex-col gap-3">
                <li v-for="key in group.bad" :key="key" class="border-l-2 border-line pl-4 text-base text-muted dark:border-gray-700 dark:text-gray-400">
                  {{ t(`coverLetterGuide.openClose.${group.id}.${key}`) }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Include / leave out -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('coverLetterGuide.includeOmit.eyebrow')"
            :title="t('coverLetterGuide.includeOmit.title')"
            width="max-w-4xl"
          />

          <div class="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
            <div class="rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800">
              <div class="display-heading display-heading-4 mb-6">{{ t('coverLetterGuide.includeOmit.includeTitle') }}</div>
              <ul class="flex flex-col gap-4">
                <li v-for="item in include" :key="item" class="flex items-start gap-3">
                  <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-xl text-accent" />
                  <span class="text-base">{{ t(`coverLetterGuide.includeOmit.include.${item}`) }}</span>
                </li>
              </ul>
            </div>

            <div class="rounded-[0.625rem] bg-ink p-[1.875rem] text-white dark:bg-gray-800">
              <div class="display-heading display-heading-4 mb-6 !text-white">{{ t('coverLetterGuide.includeOmit.omitTitle') }}</div>
              <ul class="flex flex-col gap-4">
                <li v-for="item in omit" :key="item" class="flex items-start gap-3">
                  <i class="bi bi-x-circle mt-0.5 shrink-0 text-xl text-lilac" />
                  <span class="!text-base !text-white/80">{{ t(`coverLetterGuide.includeOmit.omit.${item}`) }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- The handover into the product, in its own words -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-top">
        <div class="shell">
          <div class="mx-auto max-w-3xl text-center">
            <div class="eyebrow mb-5">{{ t('coverLetterGuide.product.eyebrow') }}</div>
            <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('coverLetterGuide.product.title') }}</h2>
            <p class="mt-5 text-lg">{{ t('coverLetterGuide.product.text') }}</p>
            <span class="mt-5 block text-sm text-muted dark:text-gray-400">{{ t('coverLetterGuide.product.free', { price: jobPackPrice, days: JOB_PACK_DAYS }) }}</span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
